import moment from 'moment';
import type { Dispatch } from 'redux';
import type { ThunkDispatch } from 'redux-thunk';
import {
  SET_PAGE,
  SET_COUNTRY_OPTIONS,
  SET_REGION_OPTIONS,
  SET_CITY_OPTIONS,
  SET_COUNTRY,
  SET_REGION,
  SET_CITY,
  SET_ARRIVAL,
  SET_DURATION,
  SET_OCCUPANCY,
  SET_FILTER_SET,
  RESET_FILTER_SET,
  ActionSetPage,
  ActionSetArrival,
  ActionSetDuration,
  ActionSetOccupancy,
  ActionSetFilterSet,
  ActionSetCountryOptions,
  ActionSetRegionOptions,
  ActionSetCityOptions,
  ActionSetCity,
  ActionResetFilterSet,
} from './types';
import I18n from '../../../config/i18n';
import store from '../../../config/redux/store';
import ApiClient from '../../../utils/ApiClient';
import { stateDiff } from '../reducers';
import type { FilterSet, RequestState } from '../types';

export function setPage(page: number): ActionSetPage {
  return {
    type: SET_PAGE,
    page,
  };
}

export function setArrival(arrival: moment.Moment | null): ActionSetArrival {
  return {
    type: SET_ARRIVAL,
    arrival: arrival && moment(arrival),
  };
}

export function setDuration(durationOfStay: number | null): ActionSetDuration {
  return {
    type: SET_DURATION,
    durationOfStay,
  };
}

export function setOccupancy(occupancy: number): ActionSetOccupancy {
  return {
    type: SET_OCCUPANCY,
    occupancy,
  };
}

export function setFilterSet(filterSet: Partial<FilterSet>): ActionSetFilterSet {
  return {
    type: SET_FILTER_SET,
    filterSet,
  };
}

export function setCountryOptions(countries: string[]): ActionSetCountryOptions {
  return {
    type: SET_COUNTRY_OPTIONS,
    countries,
  };
}

export function fetchCountryOptions() {
  return (dispatch: ThunkDispatch<RequestState, unknown, ActionSetCountryOptions>): void => {
    ApiClient.getCountryList(I18n.locale).then((res: any) => dispatch(setCountryOptions(res.data)));
  };
}

export function setRegionOptions(regions: string[]): ActionSetRegionOptions {
  return {
    type: SET_REGION_OPTIONS,
    regions,
  };
}

export const fetchRegionOptions = (countryCode: string) => (dispatch: Dispatch) => {
  ApiClient.getRegionList(I18n.locale, countryCode).then((res: any) =>
    dispatch(setRegionOptions(res.data)),
  );
};

export function setCityOptions(cities: string[]): ActionSetCityOptions {
  return {
    type: SET_CITY_OPTIONS,
    cities,
  };
}

export const fetchCityOptions = (countryCode: string, region: string) => (dispatch: Dispatch) => {
  ApiClient.getCityList(I18n.locale, countryCode, region).then((res: any) =>
    dispatch(setCityOptions(res.data)),
  );
};

export const setCountry = (countryCode: string) => (dispatch: Dispatch) => {
  dispatch({
    type: SET_COUNTRY,
    countryCode,
  });
  if (countryCode) {
    dispatch(fetchRegionOptions(countryCode) as any);
  }
};

export const setRegion = (countryCode: string, region: string) => (dispatch: Dispatch) => {
  dispatch({
    type: SET_REGION,
    countryCode,
    region,
  });
  if (region) {
    dispatch(fetchCityOptions(countryCode, region) as any);
  }
};

export function setCity(countryCode: string, region: string, city: string): ActionSetCity {
  return {
    type: SET_CITY,
    countryCode,
    region,
    city,
  };
}

const setLocation =
  ({ countryCode, region, city }: any) =>
  (dispatch: Dispatch) => {
    if (countryCode && region && (city || city === '')) {
      dispatch(fetchCityOptions(countryCode, region) as any);
      dispatch(setCity(countryCode, region, city));
    } else if (countryCode && (region || region === '')) {
      dispatch(fetchRegionOptions(countryCode) as any);
      dispatch(setRegion(countryCode, region) as any);
    } else if (countryCode || countryCode === '') {
      dispatch(setCountry(countryCode) as any);
    }
  };

export const setRequest = (request: RequestState) => (dispatch: Dispatch) => {
  const state = store.getState();
  const diff = stateDiff(request, state.request);
  if ('city' in diff || 'region' in diff || 'countryCode' in diff) {
    dispatch(setLocation(diff) as any);
  }
  if ('arrival' in diff) {
    dispatch(setArrival(diff.arrival || null));
  }
  if ('durationOfStay' in diff) {
    dispatch(setDuration(diff.durationOfStay));
  }
  if ('occupancy' in diff) {
    dispatch(setOccupancy(diff.occupancy));
  }
  if ('filterSet' in diff) {
    dispatch(setFilterSet(diff.filterSet));
  }
};

export function resetFilterSet(): ActionResetFilterSet {
  return {
    type: RESET_FILTER_SET,
  };
}

export default {
  fetchCountryOptions,
  fetchRegionOptions,
  fetchCityOptions,
  setCountry,
  setRegion,
  setCity,
  setArrival,
  setDuration,
  setOccupancy,
  setFilterSet,
  setRequest,
};
